import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function AddProduct() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [cats, setCats] = useState([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [stock, setStock] = useState(1);
  const [category, setCategory] = useState("");
  const [tags, setTags] = useState("");
  const [images, setImages] = useState([]); // storage paths
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get("/categories").then(r => { setCats(r.data); if (r.data.length) setCategory(r.data[0].slug); }); }, []);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setImages(prev => [...prev, data.path]);
      }
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!title || !price || !category) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/products", {
        title, description, price: parseFloat(price), category,
        images, stock: parseInt(stock || 1),
        tags: tags.split(",").map(t => t.trim()).filter(Boolean),
      });
      toast.success("Product created");
      await refresh();
      if (!data.is_published) {
        // immediately offer to pay listing fee
        try {
          const r = await api.post("/checkout/listing-fee", { product_id: data.id, origin_url: window.location.origin });
          window.location.href = r.data.url; return;
        } catch {}
      }
      nav("/seller");
    } catch (err) { toast.error("Failed to create"); }
    finally { setSubmitting(false); }
  };

  return (
    <div data-testid="add-product-page" className="pt-28 pb-24 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
      <p className="text-xs uppercase tracking-[0.25em] text-zinc-500">New listing</p>
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-2 mb-10">List a product</h1>

      <form onSubmit={submit} className="space-y-6">
        <div>
          <label className="text-xs uppercase tracking-widest text-zinc-500">Title</label>
          <input data-testid="ap-title" required value={title} onChange={(e) => setTitle(e.target.value)} className="input-dark mt-2" />
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-zinc-500">Description</label>
          <textarea data-testid="ap-description" rows={5} value={description} onChange={(e) => setDescription(e.target.value)} className="input-dark mt-2 resize-y" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-zinc-500">Price (USD)</label>
            <input data-testid="ap-price" required type="number" min="0.5" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} className="input-dark mt-2" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-zinc-500">Stock</label>
            <input data-testid="ap-stock" type="number" min="1" value={stock} onChange={(e) => setStock(e.target.value)} className="input-dark mt-2" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-zinc-500">Category</label>
            <select data-testid="ap-category" value={category} onChange={(e) => setCategory(e.target.value)} className="input-dark mt-2">
              {cats.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-zinc-500">Tags (comma separated)</label>
          <input data-testid="ap-tags" value={tags} onChange={(e) => setTags(e.target.value)} className="input-dark mt-2" placeholder="neon, handmade, resin" />
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-zinc-500">Images</label>
          <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {images.map((p, i) => (
              <div key={i} className="relative aspect-[4/5] border border-white/10">
                <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                <button type="button" data-testid={`remove-img-${i}`} onClick={() => setImages(images.filter((_, j) => j !== i))} className="absolute top-1 right-1 bg-black/70 p-1 hover:text-red-400">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
            <label className="aspect-[4/5] border border-dashed border-white/15 hover:border-[#FF007F] flex flex-col items-center justify-center text-zinc-500 cursor-pointer text-xs">
              {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Upload className="w-5 h-5 mb-2" />Upload</>}
              <input data-testid="ap-image-input" type="file" accept="image/*" multiple className="hidden" onChange={onUpload} disabled={uploading} />
            </label>
          </div>
        </div>

        <div className="card-surface p-4 text-sm text-zinc-400">
          {user?.seller_subscription_active
            ? <>You're a <span className="text-[#CCFF00] font-bold">Pro seller</span>. This product publishes immediately.</>
            : <>Publishing fee: <span className="text-[#FF007F] font-bold">$0.50</span> · After save, you'll be sent to Stripe checkout.</>
          }
        </div>

        <button data-testid="ap-submit" disabled={submitting} className="btn-primary w-full">
          {submitting ? "Saving…" : (user?.seller_subscription_active ? "Publish product" : "Save & pay listing fee")}
        </button>
      </form>
    </div>
  );
}
